import { DEFAULT_TIMEZONE } from '@yoyoclub/shared';
import { useEffect, useState } from 'react';

import { courseApi } from '../../services/courseApi';
import type {
  CourseSalePurchase,
  CourseSalesReport,
  CourseSalesTotals,
  PurchaseCurrency,
} from '../../services/courseApi';

interface CoursePurchasesModalProps {
  activityId: string;
  courseTitle: string;
  getToken: () => Promise<string | undefined>;
  onClose: () => void;
}

const CURRENCY_SYMBOL: Record<PurchaseCurrency, string> = { usd: '$', cny: '¥' };

function money(amount: number, currency: PurchaseCurrency): string {
  return `${CURRENCY_SYMBOL[currency] ?? ''}${(amount / 100).toFixed(2)}`;
}

// Recorded-course sales for one course: totals, per-lesson breakdown and the
// buyer-level rows behind them. Mirrors SessionBookingsModal for activities.
export default function CoursePurchasesModal({
  activityId,
  courseTitle,
  getToken,
  onClose,
}: CoursePurchasesModalProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<CourseSalesReport | null>(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const token = await getToken();
        if (!token) throw new Error('Not authenticated');
        setReport(await courseApi.hostCoursePurchases(activityId, token));
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [activityId, getToken]);

  const handleExport = async () => {
    try {
      setExporting(true);
      const token = await getToken();
      if (!token) throw new Error('Not authenticated');
      const { blob, filename } = await courseApi.hostExportCourseSales({ activityId }, token);

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to export');
    } finally {
      setExporting(false);
    }
  };

  const purchases = report?.purchases ?? [];
  const showEmail = purchases.some((p) => p.customerEmail !== undefined);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose} />

      <div className="relative mx-4 flex max-h-[90vh] w-full max-w-6xl flex-col rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between px-6 py-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{courseTitle}</h2>
            <p className="text-sm text-gray-500">Course sales</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleExport}
              disabled={loading || !report || purchases.length === 0 || exporting}
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {exporting ? 'Exporting...' : 'Export CSV'}
            </button>
            <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600">
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-auto p-6">
          {loading ? (
            <div className="flex h-64 items-center justify-center">
              <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600"></div>
            </div>
          ) : error ? (
            <div className="flex h-64 items-center justify-center text-red-600">{error}</div>
          ) : !report || purchases.length === 0 ? (
            <div className="flex h-64 items-center justify-center text-gray-500">
              No purchases yet
            </div>
          ) : (
            <div className="space-y-6">
              <Summary summary={report.summary} />
              <LessonTable lessons={report.lessons} />
              <PurchaseTable purchases={purchases} showEmail={showEmail} />
            </div>
          )}
        </div>

        {!loading && !error && purchases.length > 0 && (
          <div className="bg-gray-50 px-6 py-3 text-sm text-gray-500">
            {purchases.length} {purchases.length === 1 ? 'purchase' : 'purchases'}
          </div>
        )}
      </div>
    </div>
  );
}

function Summary({ summary }: { summary: CourseSalesReport['summary'] }) {
  return (
    <div className="rounded-lg border border-gray-200 p-4">
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <Stat label="Purchases" value={String(summary.purchaseCount)} />
        <Stat label="Buyers" value={String(summary.buyerCount)} />
        <Stat label="Whole course" value={String(summary.wholeCourseCount)} />
        <Stat label="Per-lesson" value={String(summary.perLessonCount)} />
      </div>

      <div className="mt-4 space-y-2 pt-4">
        {summary.totals.map((total) => (
          <CurrencyTotals key={total.currency} total={total} />
        ))}
      </div>

      {summary.partialRefundCount > 0 && (
        <p className="mt-3 text-xs text-amber-700">
          {summary.partialRefundCount} of these had a partial refund. Amounts are net of it.
        </p>
      )}
    </div>
  );
}

function CurrencyTotals({ total }: { total: CourseSalesTotals }) {
  return (
    <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm">
      <span className="font-medium text-gray-900">{total.currency.toUpperCase()}</span>
      <span className="text-gray-500">
        Subtotal <span className="text-gray-900">{money(total.subtotal, total.currency)}</span>
      </span>
      <span className="text-gray-500">
        Discount <span className="text-gray-900">{money(total.discount, total.currency)}</span>
      </span>
      {total.memberDiscount > 0 && (
        <span className="text-gray-500">
          Member discount (platform){' '}
          <span className="text-gray-900">{money(total.memberDiscount, total.currency)}</span>
        </span>
      )}
      <span className="text-gray-500">
        Store credit{' '}
        <span className="text-gray-900">{money(total.storeCredit, total.currency)}</span>
      </span>
      <span className="text-gray-500">
        Net charged{' '}
        <span className="font-semibold text-gray-900">
          {money(total.netCharged, total.currency)}
        </span>
      </span>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs text-gray-500">{label}</p>
      <p className="text-xl font-semibold text-gray-900">{value}</p>
    </div>
  );
}

function LessonTable({ lessons }: { lessons: CourseSalesReport['lessons'] }) {
  if (lessons.length === 0) return null;
  return (
    <div>
      <h3 className="mb-2 text-sm font-medium text-gray-700">By lesson</h3>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <Th>Lesson</Th>
              <Th>Sold individually</Th>
              <Th>Via whole course</Th>
              <Th>Paid (per-lesson)</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {lessons.map((lesson, index) => (
              <tr key={lesson.order} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                <Td>
                  Lesson {lesson.order + 1} · {lesson.title}
                </Td>
                <Td>{lesson.perLessonUnits}</Td>
                <Td>{lesson.wholeCourseUnits}</Td>
                <Td>
                  {lesson.revenue.length === 0
                    ? '-'
                    : lesson.revenue.map((r) => money(r.amount, r.currency)).join(', ')}
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PurchaseTable({
  purchases,
  showEmail,
}: {
  purchases: CourseSalePurchase[];
  showEmail: boolean;
}) {
  return (
    <div>
      <h3 className="mb-2 text-sm font-medium text-gray-700">Purchases</h3>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <Th>Customer</Th>
              {showEmail && <Th>Email</Th>}
              <Th>Phone</Th>
              <Th>Type</Th>
              <Th>Lessons</Th>
              <Th>Subtotal</Th>
              <Th>Discount</Th>
              <Th>Member discount</Th>
              <Th>Store credit</Th>
              <Th>Net charged</Th>
              <Th>Payment</Th>
              <Th>Purchased</Th>
              <Th>Note</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {purchases.map((purchase, index) => (
              <tr key={purchase.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                <Td>{purchase.customerName}</Td>
                {showEmail && <Td>{purchase.customerEmail || ''}</Td>}
                <Td>{purchase.customerPhone || ''}</Td>
                <Td>{purchase.purchaseType === 'whole' ? 'Whole course' : 'Per-lesson'}</Td>
                <Td>
                  {purchase.purchaseType === 'whole' ? 'All lessons' : purchase.lessons.join('; ')}
                </Td>
                <Td>{money(purchase.subtotal, purchase.currency)}</Td>
                <Td>{money(purchase.discount, purchase.currency)}</Td>
                <Td>{money(purchase.memberDiscount ?? 0, purchase.currency)}</Td>
                <Td>{money(purchase.storeCredit, purchase.currency)}</Td>
                <Td>{money(purchase.netCharged, purchase.currency)}</Td>
                <Td>{purchase.paymentMethod}</Td>
                <Td>
                  {new Date(purchase.completedAt).toLocaleString('en-US', {
                    timeZone: DEFAULT_TIMEZONE,
                    year: 'numeric',
                    month: 'numeric',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true,
                  })}
                </Td>
                <Td>{purchase.refundNote}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th className="whitespace-nowrap px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
      {children}
    </th>
  );
}

function Td({ children }: { children: React.ReactNode }) {
  return <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-900">{children}</td>;
}
